import { Component, OnDestroy, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SupportCenterService, ScConversation, ScMessage, ScAttachment } from '../support-center.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-support-widget',
  templateUrl: './support-widget.component.html',
  styleUrls: ['./support-widget.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class SupportWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('messagesEnd') messagesEnd!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isOpen       = false;
  loading      = false;
  sending      = false;
  draft        = '';
  pendingFile: File | null = null;

  // notification
  unreadCount  = 0;

  // history
  view: 'chat' | 'history' = 'chat';
  historyList: (ScConversation & { lastBody: string | null })[] = [];
  historyLoading = false;
  historyConv: ScConversation | null = null;
  historyMessages: ScMessage[] = [];
  historyAttachments: ScAttachment[] = [];
  historyLoaded = false;

  // null while composing a new chat — the conversation is created on the first send
  conversation: ScConversation | null = null;
  messages: ScMessage[]    = [];
  attachments: ScAttachment[] = [];
  connected   = false;
  agentOnline = false;

  private subs = new Subscription();

  constructor(
    private sc: SupportCenterService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Widget only renders when loginS.UID is set — user is already logged in here
    this.sc.connectSocket();
    this.sc.getPending(); // the widget exists only when logged in and Support Center is enabled
    this.unreadCount = this.sc.pendingCount$.value;

    this.subs.add(this.sc.pendingCount$.subscribe(count => {
      if (!this.isOpen) { this.unreadCount = count; this.cdr.detectChanges(); }
    }));

    this.subs.add(this.sc.messages$.subscribe(msgs => {
      this.messages = msgs;
      this.cdr.detectChanges();
      this.scrollToBottom();
    }));
    this.subs.add(this.sc.conversation$.subscribe(c => {
      this.conversation = c;
      this.cdr.detectChanges();
    }));
    this.subs.add(this.sc.connected$.subscribe(c => {
      this.connected = c;
      this.cdr.detectChanges();
    }));
    this.subs.add(this.sc.agentOnline$.subscribe(v => {
      this.agentOnline = v;
      this.cdr.detectChanges();
    }));

    // Agent-initiated conversation (conversation$ and messages$ are already set)
    this.subs.add(this.sc.newConversationInitiated$.subscribe(conv => {
      this.view = 'chat';
      this.attachments = [];
      if (this.isOpen) {
        this.sc.markRead(conv.id);
        this.sc.notifyCustomerOnline(conv.id);
      }
      this.cdr.detectChanges();
    }));

    // An agent replied in a conversation that isn't on screen (e.g. one that had
    // ended — the reply re-opens it). Switch to it unless a live chat is showing.
    this.subs.add(this.sc.otherConversationMessage$.subscribe(() => {
      if (this.conversation && !this.isExpired) return; // it will show in history
      this.sc.conversation$.next(null);
      this.sc.messages$.next([]);
      this.attachments = [];
      if (this.isOpen) {
        this.view = 'chat';
        this.historyConv = null;
        this.loadOrCompose();
      } else {
        this.sc.getPending();
      }
    }));

    // Another user now (impersonation): nothing shown for the previous one may remain
    this.subs.add(this.sc.identityChanged$.subscribe(() => {
      this.isOpen = false;
      this.view = 'chat';
      this.draft = '';
      this.clearFile();
      this.attachments = [];
      this.historyList = [];
      this.historyLoaded = false;
      this.historyConv = null;
      this.historyMessages = [];
      this.historyAttachments = [];
      this.unreadCount = 0;
      this.cdr.detectChanges();
    }));

    this.subs.add(this.sc.newAttachment$.subscribe(att => {
      if (!this.messages.some(m => Number(m.id) === Number(att.messageId))) return;
      if (this.attachments.some(a => Number(a.id) === Number(att.id))) return;
      this.attachments = [...this.attachments, att];
      this.cdr.detectChanges();
    }));

    // Unread notification: count new agent messages while panel is closed
    let knownAgentCount = 0;
    this.subs.add(this.sc.messages$.subscribe(msgs => {
      const agentMsgs = msgs.filter(m => m.senderType === 'agent').length;
      if (!this.isOpen && agentMsgs > knownAgentCount) {
        this.unreadCount += agentMsgs - knownAgentCount;
        this.cdr.detectChanges();
      }
      knownAgentCount = agentMsgs;
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.sc.disconnectSocket();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.unreadCount = 0;
      if (!this.conversation) this.loadOrCompose();
      else this.loadThread(this.conversation.id);
    } else {
      if (this.conversation) this.sc.notifyCustomerOffline(this.conversation.id);
    }
    this.cdr.detectChanges();
  }

  // Resume the live conversation if there is one; otherwise show an empty composer.
  private loadOrCompose(): void {
    this.loading = true;
    this.sc.getConversation().subscribe({
      next: conv => {
        if (conv && !this.sc.isExpired(conv)) {
          this.sc.conversation$.next(conv);
          this.loadThread(conv.id);
        } else {
          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      error: () => { this.loading = false; }
    });
  }

  private loadThread(id: number): void {
    this.sc.getThread(id).subscribe({
      next: ({ conversation, messages, attachments }) => {
        this.sc.conversation$.next(conversation);
        this.sc.messages$.next(messages);
        this.attachments = attachments;
        this.loading = false;
        if (this.isOpen) {
          this.sc.markRead(id);
          this.sc.notifyCustomerOnline(id);
        }
        this.scrollToBottom();
      },
      error: () => { this.loading = false; }
    });
  }

  // Clear the screen for a new chat. Nothing is created until the first message is
  // sent, so opening the widget never leaves empty conversations in the agents' inbox.
  startNew(): void {
    if (this.conversation) this.sc.notifyCustomerOffline(this.conversation.id);
    this.view = 'chat';
    this.historyConv = null;
    this.sc.conversation$.next(null);
    this.sc.messages$.next([]);
    this.sc.agentOnline$.next(false);
    this.attachments = [];
    this.cdr.detectChanges();
  }

  // ─── history ──────────────────────────────────────────────────────────────
  showHistory(): void {
    this.view = 'history';
    this.historyConv = null;
    if (!this.historyLoaded) this.loadHistory();
  }

  backToChat(): void {
    this.view = 'chat';
    this.historyConv = null;
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private loadHistory(): void {
    this.historyLoading = true;
    this.sc.listConversations().subscribe({
      next: list => {
        this.historyList = list;
        this.historyLoading = false;
        this.historyLoaded = true;
        this.cdr.detectChanges();
      },
      error: () => { this.historyLoading = false; }
    });
  }

  openHistoryConv(conv: ScConversation): void {
    const active = this.sc.conversation$.value;
    if (active && active.id === conv.id && !this.sc.isExpired(conv)) {
      this.view = 'chat';
      this.historyConv = null;
      this.cdr.detectChanges();
      this.scrollToBottom();
      return;
    }
    this.historyConv = conv;
    this.historyMessages = [];
    this.historyAttachments = [];
    this.sc.getThread(conv.id).subscribe({
      next: ({ messages, attachments }) => {
        this.historyMessages = messages;
        this.historyAttachments = attachments;
        this.cdr.detectChanges();
        this.scrollToBottom();
      }
    });
  }

  backToHistory(): void {
    this.historyConv = null;
  }

  getHistoryAttachmentsFor(messageId: number): ScAttachment[] {
    return this.historyAttachments.filter(a => a.messageId === messageId);
  }

  // ─── messaging ────────────────────────────────────────────────────────────
  get isExpired(): boolean {
    return this.conversation ? this.sc.isExpired(this.conversation) : false;
  }

  sendMessage(): void {
    const body = this.draft.trim();
    if ((!body && !this.pendingFile) || this.sending || this.isExpired) return;

    this.sending = true;
    if (this.conversation) {
      this.postMessage(this.conversation.id, body);
      return;
    }

    // First message of a new chat — create the conversation, then send
    this.sc.startConversation().subscribe({
      next: conv => {
        this.sc.conversation$.next(conv);
        this.sc.messages$.next([]);
        this.attachments = [];
        this.historyLoaded = false; // history now has one more entry
        if (this.isOpen) this.sc.notifyCustomerOnline(conv.id);
        this.postMessage(conv.id, body);
      },
      error: () => { this.sending = false; }
    });
  }

  private postMessage(conversationId: number, body: string): void {
    this.sc.sendMessageHttp(conversationId, body || '📎', this.sc.newClientMsgId()).subscribe({
      next: msg => {
        if (!this.sc.messages$.value.some(m => m.id === msg.id)) {
          this.sc.messages$.next([...this.sc.messages$.value, msg]);
        }
        this.draft = '';
        const ta = document.querySelector<HTMLTextAreaElement>('.sc-textarea');
        if (ta) { ta.style.height = 'auto'; ta.rows = 2; }
        if (this.pendingFile) {
          this.sc.uploadAttachment(conversationId, msg.id, this.pendingFile).subscribe({
            next: att => {
              this.attachments = [...this.attachments, att];
              this.clearFile();
              this.sending = false;
            },
            error: () => { this.sending = false; }
          });
        } else {
          this.sending = false;
        }
      },
      error: () => { this.sending = false; }
    });
  }

  // Enter sends, Shift+Enter inserts a newline
  onEnter(e: KeyboardEvent): void {
    if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
    e.preventDefault();
    this.sendMessage();
  }

  autoGrow(e: Event): void {
    const el = e.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }

  onFileSelected(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (input.files?.length) this.pendingFile = input.files[0];
  }

  clearFile(): void {
    this.pendingFile = null;
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  getAttachmentsFor(messageId: number): ScAttachment[] {
    return this.attachments.filter(a => a.messageId === messageId);
  }

  openAttachment(att: ScAttachment): void {
    this.sc.getAttachmentUrl(att.id).subscribe(({ url }) => window.open(url, '_blank'));
  }

  private scrollToBottom(): void {
    setTimeout(() => this.messagesEnd?.nativeElement?.scrollIntoView({ behavior: 'smooth' }), 80);
  }
}
